'use strict';

import React, {Component, useState} from "react";
import {observer} from "mobx-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import moment from "moment";

import {FaAngleDown, FaAngleUp, FaExclamationTriangle, FaLink} from "react-icons/fa";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from 'react-bootstrap/Form';
import ListGroup from "react-bootstrap/ListGroup";
import Accordion from 'react-bootstrap/Accordion';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import Lists from '@uw-it-sis/lib-react/lib/Lists';
import {DaysOfWeek, States} from "@uw-it-sis/lib-react/lib/AppConstants";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import {RegEx, handleInputMaxLength, handleInputNumbersOnly} from "@uw-it-sis/lib-react/lib/ValidationUtils";
import {isEmpty, isFalse, makeId} from "@uw-it-sis/lib-react/lib/Utils";

import {normalize, daysOfWeekAbbreviation} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";
import SectionCode from "@uw-it-sis/lib-react-myplan/lib/SectionCode";
import RegistrationCodeLink from "@uw-it-sis/lib-react-myplan/lib/RegistrationCodeLink";

import ScheduleBuilderOptionCourseStore from "./ScheduleBuilderOptionCourseStore";
import RegisterButtonStore from "../common/RegisterButtonStore";

const EntryCodeInputControl = (props) => {
    const [isInvalid, setIsInvalid] = useState(false);

    const onEntryCodeChange = (e) => {
        e.stopPropagation();
        let sln = e.target.name;
        let entryCode = e.target.value;
        RegisterButtonStore.updateEntryCode(sln, entryCode);

        if (isInvalid) {
            validateEntryCode(e);
        }
    }

    const validateEntryCode = (e) => {
        const target = e.currentTarget;
        const value = target.value;
        // Check is value is empty
        let valueEmpty = (value === "");
        let valueInvalid = isFalse(RegEx.sln.test(value));

        setIsInvalid(valueEmpty || valueInvalid);
    }

    return (
        <Form.Group controlId={"entry-code-" + makeId(props.activity.activityOfferingId)} className="mt-2 mb-0">
            <Form.Label className="mb-0">
                <span className="sr-only">{props.activity.courseCd} {props.activity.activityCode} </span>Add/Faculty code <span className="text-danger">(Required)</span>
            </Form.Label>
            <Form.Control
                name={props.activity.registrationCode}
                type="number"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                maxLength={5}
                pattern="[0-9]*"
                required={true}
                className="w-md-25 w-sm-50 w-100"
                onBlur={validateEntryCode}
                onChange={onEntryCodeChange}
                onInput={handleInputMaxLength}
                onKeyDown={handleInputNumbersOnly}
                aria-label={`{props.activity.courseCd} {props.activity.activityCode} Add/Faculty code`}
                aria-invalid={isInvalid}
                isInvalid={isInvalid}/>
            <Form.Control.Feedback type="invalid">
                Must enter 5 digit code
            </Form.Control.Feedback>
        </Form.Group>
    );
}

EntryCodeInputControl.propTypes = {
    activity: PropTypes.object.isRequired
}

/**
 * Build a course item for display within a schedule built in the schedule details
 */
class ScheduleBuilderOptionCourse extends Component {

    constructor(props) {
        super(props);
        this.props = props;
        this.store = new ScheduleBuilderOptionCourseStore();
    }

    componentDidMount() {
        this.store.mount(this.props);
    }

    componentDidUpdate(prevProps) {
        if (this.props.item !== prevProps.item) {
            this.store.mount(this.props);
        } else if(this.props.showSendToRegistration !== prevProps.showSendToRegistration) {
            this.store.mount(this.props);
        }
    }

    onChange(e, activity) {
        e.stopPropagation();
        this.store.updateSelection(e.target.name, e.target.checked, activity);
    }

    render() {
        let courseActivities = [];
        const showAlternates = this.props.showAlternates;
        if (this.store.state === States.done) {
            courseActivities = this.store.getCourseActivities();
        }

        // Build the list item for each section
        let courseActivitiesList = courseActivities.map((activity, index) => {

            let sectionCode = `${this.props.item.courseCd} ${activity.activityCode}`;
            let enrollStatus = null;
            let enrollNumbers = null;

            let enrollStatusValue = activity.enrollStatus ? activity.enrollStatus.toLowerCase() : "";
            // enrollStatus can be "Open/Closed/Suspended/Withdrawn", so slightly different then other places where stateKey is used
            if (enrollStatusValue !== "") {
                let enrollBadgeVariant;
                switch (enrollStatusValue) {
                    case "closed":
                        enrollBadgeVariant = "danger";
                        break;
                    case "open":
                        enrollBadgeVariant = "success";
                        break;
                    case "suspended":
                    case "withdrawn":
                        enrollBadgeVariant = "uw-metallic-gold";
                        break;
                    default:
                        enrollBadgeVariant = "white";
                }

                enrollStatus =
                    <>
                        <span className="sr-only">Enrollment Status: </span>
                        <Badge bg={enrollBadgeVariant} className="fs-9 text-uppercase">
                            {enrollStatusValue}
                        </Badge>
                    </>;

                if ( enrollStatusValue === 'open' || enrollStatusValue === 'closed' ) {
                    enrollNumbers =
                        <Badge bg="light-gray" className="text-dark fs-9 text-uppercase fw-normal border">
                            <span>{activity.openSeats}</span> <span className="sr-only">seats available </span>
                            <abbr title="available" aria-hidden={true}>avail</abbr> of {activity.totalSeats}
                        </Badge>;
                }
            }

            let enrollment =
                <div className="d-flex align-items-center">
                    {enrollStatus}
                    {enrollNumbers}
                </div>;

            const sectionUniqueId = activity.activityOfferingId.replace(/\:/g, "-").replace(/\s+|&/g, "");
            let classMeetingTimes = activity.classMeetingTimes.map((meeting, index) => {
                let instructor = null;
                // Build array of all instructors names without duplicates
                let allInstructors = meeting.instructorName ? meeting.instructorName.split('|') : undefined;

                if ( allInstructors && allInstructors.length ) {
                    if ( allInstructors.length > 1 ) {
                        const InstructorsToggle = ({children, eventKey}) => {
                            const decoratedOnClick = useAccordionButton(eventKey, () => {});

                            return (
                                <Button variant="accordion-truncate"
                                        onClick={decoratedOnClick}/>
                            )
                        };

                        instructor =
                            <div className="mb-1">
                                <Accordion className="accordion-truncate">
                                    <Accordion.Collapse eventKey={"instructors-list-" + sectionUniqueId}>
                                        <>Instructor:<Lists data={allInstructors} className="mb-1" unstyled /></>
                                    </Accordion.Collapse>
                                    <InstructorsToggle eventKey={"instructors-list-" + sectionUniqueId} />
                                </Accordion>
                            </div>;
                    } else {
                        let instructorName = allInstructors.length === 1 && allInstructors[0] ? allInstructors[0] : '--';
                        instructor =
                            <div className="mb-1">
                                Instructor: {instructorName}
                            </div>;
                    }
                }

                // If section is arranged, show meeting details, if not show TBA message
                if (meeting.arranged) {
                    let meetingDays = DaysOfWeek.filter(day => meeting[day.key]).map(day => {
                        return day.shortCode;
                    });

                    let building = !meeting.building ? "" : meeting.building;
                    let location = !meeting.location ? "" : meeting.location;
                    let meetingLocation = `${building} ${location}`;

                    return (
                        <div key={index}>
                            <div className="d-sm-flex justify-content-sm-between">
                                <div className="d-inline-block me-2 me-sm-0">{daysOfWeekAbbreviation(meetingDays.join(''))}</div>
                                <div className="d-inline-block">
                                    <time dateTime={moment(meeting.startTime, "h:mm A").format("HH:mm")}>{meeting.startTime}</time>
                                    <span> - </span>
                                    <time dateTime={moment(meeting.endTime, "h:mm A").format("HH:mm")}>{meeting.endTime}</time>
                                </div>
                                <div className="d-block"><span className="sr-only">Location: </span>{meetingLocation}</div>
                            </div>
                            {instructor}
                        </div>

                    );
                } else {
                    return (
                        <div key={index}>To be arranged</div>
                    );
                }
            });

            let activityCodeComponent;
            let activityCode = <SectionCode section={activity}
                                            additionalAnalytics={{context: 'ScheduleBuilder-link'}}
                                            isDupEnroll={activity.duplicateEnrollmentAllowed}
                                            isTimeMask={activity.timeMaskFlag}
                                            isScheduleOption
                                            inline />;

            if (
                (
                    (!activity.primary && this.props.alternatePrimaryActivityIds.includes(activity.parentActivityId))
                    ||
                    // If the activity is primary and has no secondaries, check to see if it is in the list of
                    // alternate primary activities or if it is the 'root' primary activity
                    // and included in the set of primaries that have alternates
                    (activity.primary && isEmpty(activity.secondaryOptions) &&
                        ( this.props.alternatePrimaryList.includes(activity.parentActivityId) ||
                          this.props.alternatePrimaryActivityIds.includes(activity.parentActivityId)))
                )
                &&
                !this.props.showSendToRegistration // Alternates have be selected before starting registration process
            ) {
                let checked = this.props.selectedAlternateCodes.includes(sectionCode);

                let name = (activity.primary) ? activity.courseCd : activity.parentActivityId;

                let formValue = `${activity.parentActivityId}|${sectionCode}${activity.primary ? "|primary" : ""}`;

                activityCodeComponent =
                    <Form.Check id={'select-section-' + activity.activityOfferingId.replace(/:/g, "-")}>
                        <Form.Check.Input type="radio"
                                          name={name}
                                          value={formValue}
                                          checked={checked}
                                          onChange={this.props.onSelectAlternate} />
                        <Form.Check.Label><span className="sr-only">Section </span>{activityCode}</Form.Check.Label>
                    </Form.Check>;
            } else {
                activityCodeComponent = <><span className="sr-only">Section </span>{activityCode}</>;
            }

            let sectionClasses = null;
            let name = this.store.getActivityName(activity);
            let selectedActivity = this.store.data.selectedActivities[name] !== undefined;
            if (this.props.showPlanned) {
                sectionClasses = classnames(
                    {"bg-planned": activity.planItemId !== null},
                    "empty-0",
                    "list-group-item"
                );
            } else {
                sectionClasses = classnames(
                    {"border-bottom border-dotted border-top-0 border-end-0 border-start-0 pb-2 mb-2": activity.alternateActivities.length > 0 && showAlternates}
                );
            }
            // Checkbox options is for calendar event section selections, not in reg mode
            if (this.props.allowAddRemoveSection && !this.props.showSendToRegistration) {
                activityCodeComponent = <Form.Group>
                    <Form.Check id={'add-remove-section-' + activity.activityOfferingId.replace(/:/g, "-")}
                                type="checkbox"
                                inline>
                        <Form.Check.Input type="checkbox"
                                          name={name}
                                          className="me-2 fs-5"
                                          disabled={appStore.isAdviser()}
                                          checked={selectedActivity}
                                          onChange={(e) => this.onChange(e, activity)} />
                        <Form.Check.Label><><span className="sr-only">Section </span>{activityCode}</></Form.Check.Label>
                    </Form.Check>
                </Form.Group>
            }

            const sectionCodeWidth = "64px";

            let warnDuplicates;
            if(this.store.getDuplicateSecondaries() !== undefined && this.store.getDuplicateSecondaries()[name]) {
                warnDuplicates =
                    <>
                        <FaExclamationTriangle className="me-2 mt-1 text-warning" aria-hidden={true} />
                        <span>You have selected too many {this.store.getDuplicateSecondaries()[name].activityTypeDescription} sections.</span>
                    </>
            }

            let summerBadge;
            // If term is a summer term, create badge to indicate the summer period (either full, a, or b)
            if (activity.hasOwnProperty("summerSubTerm") && TermUtils.makeTermName(activity.termId) === TermUtils.TERMS["3"]) {
                // If summerSubTerm is empty, then its summer full
                let summerBadgeText = (activity.summerSubTerm !== "" ? activity.summerSubTerm : "full-term");

                summerBadge =
                    <>
                        <span className="sr-only">Summer Term: </span>
                        <Badge bg="light-gray"
                               className="text-dark align-middle fs-9 text-uppercase fw-normal border ms-2">
                            {summerBadgeText}
                        </Badge>
                    </>;
            }

            let blockReg;
            if (activity.blockParentCourseCode && activity.blockParentSectionCode && activity.blockRegistrationType) {
                // show icon and block reg type with section code of block parent
                blockReg =
                    <div className="d-flex align-items-baseline">
                        <FaLink aria-hidden="true" flip="horizontal" className="text-muted me-2" />
                        {activity.blockRegistrationType} {activity.blockParentSectionCode}
                    </div>
            }

            let entryCode;
            // If in reg mode and section requires an add code, show input for 5 digit input
            if ( activity.addCodeRequired && this.props.showSendToRegistration ) {
                entryCode =
                    <EntryCodeInputControl activity={activity} />;
            }
            // Reg mode and schedule sent to SPS, show list of items sent to SPS
            if (this.props.showSendToRegistration && this.props.dataSubmitted) {
                return (
                    <tr key={index}>
                        <td>{activity.courseCd} {activity.activityCode}</td>
                        <td>{activity.registrationCode}</td>
                    </tr>
                );
            } else {
                return (
                    <li key={index} data-planned={activity.planItemId !== null} className={sectionClasses}>
                        {warnDuplicates}
                        <div className="d-flex align-items-start">
                            <div className="scheduleBuilderSectionCode text-nowrap" style={{width: sectionCodeWidth, minWidth: sectionCodeWidth, paddingTop: '2px'}}>
                                {activityCodeComponent}
                            </div>
                            <div className="d-flex flex-column flex-grow-1">
                                <div className="d-sm-flex justify-content-sm-between align-items-sm-center">
                                    <div className="d-flex align-items-center">
                                        <RegistrationCodeLink sln={activity.registrationCode} qtryr={TermUtils.makeQtrYearFromTermId(activity.termId)} className="align-middle me-2" />
                                        <span className="sr-only">Section Type: </span>
                                        <Badge bg="light-gray"
                                               className="text-dark align-middle fs-9 text-uppercase fw-normal border">
                                            {activity.activityTypeDescription}
                                        </Badge>
                                        {summerBadge}
                                    </div>
                                    {enrollment}
                                </div>
                                {blockReg}
                                {classMeetingTimes}
                                {entryCode}
                            </div>
                        </div>
                    </li>
                );
            }
        });

        let displayAlternatesLink = undefined;
        if (this.props.hasAlternates && !this.props.showSendToRegistration) {
            let displayAlternatesText, AlternatesIcon,  displayAlternatesInfo;

            if (showAlternates) {
                AlternatesIcon = FaAngleUp;
                displayAlternatesText = `Hide alternate sections`;
                // alternate info text is only displayed in schedule detail view
                displayAlternatesInfo = this.props.allowAddRemoveSection ? null : <p className="mb-2"><small>You can change the default selection to an alternate section above when adding this schedule to plan.</small></p>;
            } else {
                AlternatesIcon = FaAngleDown;
                displayAlternatesText = `Show alternate sections that are scheduled at the same time`;
            }

            displayAlternatesLink = <div className="mt-1">
                {displayAlternatesInfo}
                <Button variant="inline-link" value={this.props.item.activityOfferingId} onClick={ (e) => this.props.onToggleAlternates(e)} className="d-inline-flex align-items-center">
                    <AlternatesIcon aria-hidden="true"  className="me-1" />
                    {displayAlternatesText}
                </Button>
            </div>;
        }

        let cdpUrl = `/course/#/courses/${normalize(this.props.item.courseCd)}`;

        let courseHeading = undefined;
        if (this.props.showCourseHeading) {
            courseHeading =
                <div className="mb-3">
                    <strong>
                        <a href={cdpUrl}>
                            {this.props.item.courseCd} {this.props.item.courseTitle} ({this.props.item.courseCredit})
                        </a>
                    </strong>
                </div>;
        }

        let courseErrorMessages;
        if (this.props.errorMessages && !this.props.showSendToRegistration) {
            let errorMessagesList = [];
            for (const errorType in this.props.errorMessages) {
                this.props.errorMessages[errorType].map(error => {
                    errorMessagesList.push(error);
                });
            }

            courseErrorMessages =
                <div className="mb-2 d-flex align-items-start">
                    <FaExclamationTriangle className="text-warning me-2 mt-1" aria-hidden={true} />
                    <Lists className="text-danger mb-0" data={errorMessagesList} unstyled />
                </div>;
        }
        // Completion of reg and summary of sections sent to SPS is shown.
        if (this.props.dataSubmitted && this.props.showSendToRegistration) {
            return courseActivitiesList;
        } else {
            return (
                <ListGroup.Item as="li" className={classnames({"border-0 p-0": !this.props.showBorder})}>
                    <Form>
                        {courseHeading}
                        {courseErrorMessages}
                        <ul className="list-unstyled mb-0 list-add-space">
                            {courseActivitiesList}
                        </ul>
                    </Form>
                    {displayAlternatesLink}
                </ListGroup.Item>
            );
        }
    }
}

ScheduleBuilderOptionCourse.propTypes = {
    item: PropTypes.object.isRequired,
    preparedActivities: PropTypes.array.isRequired,
    alternatePrimaryList: PropTypes.array,
    onToggleAlternates: PropTypes.func.isRequired,
    showAlternates: PropTypes.bool,
    hasAlternates: PropTypes.bool.isRequired,
    onSelectAlternate: PropTypes.func.isRequired,
    showSendToRegistration: PropTypes.bool.isRequired,
    dataSubmitted: PropTypes.bool.isRequired,
    selectedAlternateCodes: PropTypes.array.isRequired,
    alternatePrimaryActivityIds: PropTypes.array.isRequired,
    showCourseHeading: PropTypes.bool.isRequired,
    allowAlternateSelection: PropTypes.bool.isRequired,
    showPlanned: PropTypes.bool.isRequired,
    showBorder: PropTypes.bool.isRequired,
    allowAddRemoveSection: PropTypes.bool.isRequired,
    planData: PropTypes.array.isRequired,
    errorMessages: PropTypes.object
};

ScheduleBuilderOptionCourse.defaultProps = {
    onSelectAlternate: () => {}, // No-op default function
    alternatePrimaryList: [],
    showAlternates: false,
    hasAlternates: false,
    showSendToRegistration: false,
    dataSubmitted: false,
    selectedAlternateCodes: [],
    alternatePrimaryActivityIds: [],
    showCourseHeading: true,
    allowAlternateSelection: true,
    showPlanned: false,
    showBorder: false,
    allowAddRemoveSection: false
};

export default observer(ScheduleBuilderOptionCourse);